import { create } from 'zustand';
import type { GQLMessageItem } from '../hooks/graphql/contacts/useContact';

/**
 * Lightweight global store backing the `<GlobalEmailPreviewModal>`.
 *
 * Any call site that has either a full `GQLMessageItem` OR just an HTML
 * blob can push it here; the singleton modal mounted at the app root
 * reads the store and renders the email viewer. Replaces the old
 * `previewEmail()` open-new-tab behavior so every "click to preview an
 * email" surface gets the same in-app modal treatment.
 */
export type EmailPreviewPayload = Partial<GQLMessageItem> & {
  bodyHtml?: string | null;
  body?: string | null;
  /** Contact display name shown in the To row (outbound) / From row
   *  (inbound). Pulled from the activity's `contactName` field. */
  contactName?: string;
  /** Contact email — Recent Activities feed exposes this as
   *  `contactEmail` per row; when available we show it alongside the
   *  contact name instead of the raw to/from address. */
  contactEmail?: string;
};

interface EmailPreviewState {
  email: (GQLMessageItem & { contactName?: string; contactEmail?: string }) | null;
  open: (payload: EmailPreviewPayload | string) => void;
  close: () => void;
}

const EMPTY_MESSAGE: GQLMessageItem = {
  messageId: '',
  conversationId: '',
  channel: 'email',
  direction: 'outbound',
  fromAddress: '',
  toAddress: '',
  subject: '',
  body: '',
  bodyHtml: null,
  status: '',
  eventType: null,
  templateName: null,
  campaignId: null,
  campaignStatus: null,
  scheduledTime: null,
  deliveredAt: null,
  openedAt: null,
  createdAt: '',
  sentAt: null,
};

export const useEmailPreviewStore = create<EmailPreviewState>((set) => ({
  email: null,
  open: (payload) => {
    if (typeof payload === 'string') {
      set({ email: { ...EMPTY_MESSAGE, bodyHtml: payload } });
      return;
    }
    set({ email: { ...EMPTY_MESSAGE, ...payload } as GQLMessageItem });
  },
  close: () => set({ email: null }),
}));
